import { FC, useMemo, useEffect } from "react";
import NcImage from "shared/NcImage/NcImage";
import Glide from "@glidejs/glide";
import { useDispatch } from "react-redux";
import { setMood } from "application/features/search/searchSlice";
import { useNavigate } from "react-router-dom";
import { stockTypeToSlug } from "data/stock_types";
import { StockNumericType } from "data/enums";
import { useWindowSize } from "react-use";
import surfer from "images/tribyou_decorations/shaka.png";
import business from "images/tribyou_decorations/business-trip.png";
import casual from "images/tribyou_decorations/sweatshirt.png";
import { useTranslation } from "react-i18next";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import { MOOD_DATA } from "data/site_content";

interface MoodSliderProps {
  className?: string;
  show?: boolean;
  onStepCompleted?: () => void;
  closeModal: () => void;
}

const MoodSlider: FC<MoodSliderProps> = ({ className = "", onStepCompleted, closeModal }) => {
  
  const { t, ready } = useTranslation("translation");
  const translatedMoods = t("translation:home.moods.list", { returnObjects: true }) as any[];

  const assembledMoods: { id: number; img: string; title: string; description: string, value: string, actionButton: string }[]  = MOOD_DATA ? 
    Object.keys(MOOD_DATA).map(
      (key, index) => {
        return {
          id: index,
          img: MOOD_DATA[key],
          title: translatedMoods[index].title,
          description: translatedMoods[index].description,
          actionButton: translatedMoods[index].actionButton,
          value: translatedMoods[index].title.toLowerCase(),
        }
      }
  ) : [];

  let MY_GLIDEJS = useMemo(() => {
    return new Glide(`.SectionSliderNewCategories__MoodSlider`, {
      perView: 3,
      gap: 32,
      bound: true,
      breakpoints: {
        1280: {
          perView: 3,
        },
        1024: {
          gap: 20,
          perView: 1,
        },
        768: {
          gap: 20,
          perView: 1,
        },
        640: {
          gap: 20,
          perView: 1,
        },
        500: {
          gap: 20,
          perView: 1,
        },
      },
    });
  }, []);

  useEffect(() => {
    if (MY_GLIDEJS) {
      setTimeout(() => {
        MY_GLIDEJS.mount();
      }, 100);
    }
  }, [MY_GLIDEJS]);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { width } = useWindowSize();

  const updateMoodField = (value: string) => {
    dispatch(setMood(value));
    if (width > 768) {
      closeModal();
      navigate(`/${stockTypeToSlug(StockNumericType.STAY)}?searchType=all&mood=${value}`)
    }
  }

  if (!ready) return null;

  return (
    <>
      <span className="font-bold mb-10 text-2xl">
        {t("translation:home.moods.title")}
      </span>
      <div className={`SectionSliderNewCategories__MoodSlider w-full`}>
        <div className="flex">
          {
            width < 768 && (
              <div
                className={`nc-NextPrev relative flex items-center text-neutral-900 dark:text-neutral-300 ${className}`}
                data-nc-id="PrevPrev"
                data-glide-el="controls"
              >
                <button
                  className={` bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-6000 dark:hover:border-neutral-500 rounded-full flex items-center justify-center hover:border-neutral-300`}
                  title="Prev"
                  data-glide-dir="<"
                >
                  <i className="las la-angle-left"></i>
                </button>
              </div>
            )
          }

          <div className="glide__track" data-glide-el="track">
            <ul className="glide__slides">
              {assembledMoods.map((item) => (
                <li className="glide__slide flex flex-col items-center justify-center max-w-xs md:max-w-sm px-4" key={`mood-slider-${item.id}`}>
                  <NcImage
                    containerClassName="rounded-full w-40 h-auto"
                    src={item.img}
                    alt={item.title}
                  />
                  <span className="block text-center mt-2 w-40 text-xl font-bold">{item.title}</span>
                  <span className="text-base md:text-sm text-center text-neutral-500 dark:text-neutral-400 mb-3 min-h-24">
                    {item.description}
                  </span>
                  <ButtonPrimary onClick={() => updateMoodField(item.value)} className="!p-[0.8rem]">
                    {item.actionButton}
                  </ButtonPrimary>
                </li>
              ))}
            </ul>
          </div>
          {
            width <= 768 && (
              <div
                className={`nc-NextPrev relative flex items-center text-neutral-900 dark:text-neutral-300 ${className}`}
                data-nc-id="NextPrev"
                data-glide-el="controls"
              >
                <button
                  className={` bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-6000 dark:hover:border-neutral-500 rounded-full flex items-center justify-center hover:border-neutral-300`}
                  title="Next"
                  data-glide-dir=">"
                >
                  <i className="las la-angle-right"></i>
                </button>
              </div>
            )
          }
        </div>
        {
          width < 768 && (
            <div
              className="mt-10 glide__bullets flex items-center justify-center"
              data-glide-el="controls[nav]"
            >
              {assembledMoods.map((item, index) => (
                <div
                  key={item.id}
                  className="glide__bullet w-2 h-2 rounded-full bg-neutral-300 mx-1 focus:outline-none"
                  data-glide-dir={`=${index}`}
                ></div>
              ))}
            </div>
          )
        }

      </div>
    </>
  )
}

export default MoodSlider;